import * as ApiCalls from "../../apiCalls";
import * as BmpFcsInventoryConstants from "../../bmpfcs/bmpFcsInventoryConstants";
import { DashboardConfig, InsightData } from "../../types/dashboardConfig";
import { FilterSelect } from "./filters/FilterSelect";
const InsightWidgetFunctions = require("../../general/widgets/insightWidgetFunctions");

interface TypeCount {
  key: string;
  displayName: string;
  value: number;
}

interface CofDistribution {
  key: string;
  displayName: string;
  value: number;
  percentage: number;
}

interface MonthlyCount {
  month: string;
  value: number;
}

interface ResultDistribution {
  key: string;
  displayName: string;
  value: number;
  percentage: number;
}

interface LidSbmpInsightData {
  focus: LidSbmpYearData | null;
  comparison: LidSbmpYearData | null;
}

interface LidSbmpYearData {
  year: number;
  sbmpCount: number;
  decentralizedCount: number;
  centralizedCount: number;
  decentralizedPercentage: number;
  centralizedPercentage: number;
  decentralizedCountByType: TypeCount[];
  centralizedCountByType: TypeCount[];
  cofDistribution: CofDistribution[];
  monthlyInspectionCount: MonthlyCount[];
  inspectionCount: number;
  inspectionResultDistribution: ResultDistribution[];
}

const cofOptions = [
  { name: "All", value: "total" },
  { name: "Very High", value: "very-high" },
  { name: "High", value: "high" },
  { name: "Moderate", value: "moderate" },
  { name: "Low", value: "low" },
  { name: "Very Low", value: "very-low" },
  { name: "Unknown", value: "unknown" },
];

const projectAssociationOptions = [
  { name: "All", value: "total" },
  { name: "Yes", value: "yes" },
  { name: "No", value: "no" },
];

export const lidSbmpDashboardConfig: DashboardConfig = {
  dashboardType: "lid-bmp",
  getInsightDataFunction: async (params: Record<string, unknown>) => {
    return await ApiCalls.getLidSbmpInsight({
      ...params,
      spatialDataTitle: InsightWidgetFunctions.getSpatialDataTitle(),
    });
  },
  getReportingYearsFunction: async () => {
    const reports = await ApiCalls.getAnnualReports("lid");
    return reports;
  },
  getFilterOptionsFunction: (filterType: string) => {
    if (filterType === "cof") {
      return cofOptions;
    }
    if (filterType === "maintainedBy") {
      const options = [...BmpFcsInventoryConstants.responsiblePartyMaintenanceOptions];
      options.unshift({ name: "All", value: "total" });
      return options;
    }
    if (filterType === "projectAssociation") {
      return projectAssociationOptions;
    }
    if (filterType === "spatialItem") {
      return InsightWidgetFunctions.getSpatialDataOptions();
    }
    return [];
  },
  widgetConfigs: [
    // Row 1: Total Count, Inspection Count, Decentralized sBMPs, Centralized sBMPs
    {
      type: "numberCard",
      props: {
        title: "Total Count of Structural BMPs",
        description: "sBMP inventory",
        unit: "sBMPs",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as LidSbmpInsightData;
        const focusDataNumber = data.focus?.sbmpCount ?? 0;
        const comparisonDataNumber = data.comparison?.sbmpCount ?? 0;
        const focusSubtitle = String(data.focus?.year ?? "");
        const comparisonSubtitle = String(data.comparison?.year ?? "");

        return {
          focusDataNumber,
          comparisonDataNumber,
          focusSubtitle,
          comparisonSubtitle,
        };
      },
    },
    {
      type: "numberCard",
      props: {
        title: "Inspection Count",
        description: "Completed inspections to date",
        unit: "Inspections",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as LidSbmpInsightData;
        const focusDataNumber = data.focus?.inspectionCount ?? 0;
        const comparisonDataNumber = data.comparison?.inspectionCount ?? 0;
        const focusSubtitle = String(data.focus?.year ?? "");
        const comparisonSubtitle = String(data.comparison?.year ?? "");

        return {
          focusDataNumber,
          comparisonDataNumber,
          focusSubtitle,
          comparisonSubtitle,
        };
      },
    },
    {
      type: "numberCard",
      props: {
        title: "Decentralized sBMP Count",
        description: "Smaller-scale, distributed structural controls",
        unit: "sBMPs",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as LidSbmpInsightData;
        const focusDataNumber = data.focus?.decentralizedCount ?? 0;
        const comparisonDataNumber = data.comparison?.decentralizedCount ?? 0;
        const focusSubtitle = String(data.focus?.year ?? "");
        const comparisonSubtitle = String(data.comparison?.year ?? "");

        return {
          focusDataNumber,
          comparisonDataNumber,
          focusSubtitle,
          comparisonSubtitle,
        };
      },
    },
    {
      type: "numberCard",
      props: {
        title: "Centralized sBMP Count",
        description: "Larger-scale, regional structural controls",
        unit: "sBMPs",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as LidSbmpInsightData;
        const focusDataNumber = data.focus?.centralizedCount ?? 0;
        const comparisonDataNumber = data.comparison?.centralizedCount ?? 0;
        const focusSubtitle = String(data.focus?.year ?? "");
        const comparisonSubtitle = String(data.comparison?.year ?? "");

        return {
          focusDataNumber,
          comparisonDataNumber,
          focusSubtitle,
          comparisonSubtitle,
        };
      },
    },
    // Row 2: Inspections Over Time, Inspection Results
    {
      type: "comparisonAreaChart",
      colSpan: 2,
      props: {
        title: "Cumulative Inspection Trend",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as LidSbmpInsightData;
        const focusYear = data.focus?.year ?? 0;
        const comparisonYear = data.comparison?.year ?? 0;
        return {
          focusData: data.focus?.monthlyInspectionCount ?? [],
          comparisonData: data.comparison?.monthlyInspectionCount ?? [],
          focusYear,
          comparisonYear,
          description: `Seasonal progression of field inspections (${focusYear} vs. ${comparisonYear})`,
        };
      },
    },
    {
      type: "stackedPercentageBarChart",
      props: {
        title: "Asset Condition & Maintenance Needs",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as LidSbmpInsightData;
        const focusYear = data.focus?.year ?? 0;
        const comparisonYear = data.comparison?.year ?? 0;
        return {
          focusData: data.focus?.inspectionResultDistribution ?? [],
          comparisonData: data.comparison?.inspectionResultDistribution ?? [],
          focusYear,
          comparisonYear,
          description: `Percentage of inspected sBMPs requiring corrective action (${focusYear} vs. ${comparisonYear})`,
        };
      },
    },
    // Row 3: Decentralized sBMPs by Type, Centralized sBMPs by Type
    {
      type: "horizontalBarChart",
      props: {
        title: "Decentralized sBMP Portfolio Composition",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as LidSbmpInsightData;
        const focusYear = data.focus?.year ?? 0;
        const comparisonYear = data.comparison?.year ?? 0;
        return {
          focusData: data.focus?.decentralizedCountByType ?? [],
          comparisonData: data.comparison?.decentralizedCountByType ?? [],
          focusYear,
          comparisonYear,
          description: `Inventory volume by asset type (${focusYear} vs. ${comparisonYear})`,
        };
      },
    },
    {
      type: "horizontalBarChart",
      props: {
        title: "Centralized sBMP Portfolio Composition",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as LidSbmpInsightData;
        const focusYear = data.focus?.year ?? 0;
        const comparisonYear = data.comparison?.year ?? 0;
        return {
          focusData: data.focus?.centralizedCountByType ?? [],
          comparisonData: data.comparison?.centralizedCountByType ?? [],
          focusYear,
          comparisonYear,
          description: `Inventory volume by asset type (${focusYear} vs. ${comparisonYear})`,
        };
      },
    },
    // Row 4: Consequence of Failure
    {
      type: "pieChart",
      props: {
        title: "sBMP Consequence of Failure Distribution",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as LidSbmpInsightData;
        const year = data.focus?.year ?? 0;
        return {
          data: data.focus?.cofDistribution ?? [],
          year,
          description: `Classification of assets by the severity of impact should they fail (${year})`,
        };
      },
    },
  ],
  configurableFilters: [
    {
      component: FilterSelect,
      props: {
        label: "Consequence of Failure",
        placeholder: "Select COF",
      },
      stateKey: "cof",
      initialValue: "total",
      options: cofOptions,
    },
    {
      component: FilterSelect,
      props: {
        label: "Project Association",
        placeholder: "Select association",
      },
      stateKey: "projectAssociation",
      initialValue: "total",
      options: projectAssociationOptions,
    },
    {
      component: FilterSelect,
      get props() {
        return {
          label: InsightWidgetFunctions.getSpatialDataTitle(),
          placeholder: "Select spatial filter",
        };
      },
      stateKey: "spatialItem",
      initialValue: "total",
      get options() {
        return InsightWidgetFunctions.getSpatialDataOptions();
      },
    },
    {
      component: FilterSelect,
      props: {
        label: "Maintained By",
        placeholder: "Select maintainer",
      },
      stateKey: "maintainedBy",
      initialValue: "total",
      options: (() => {
        const options = [...BmpFcsInventoryConstants.responsiblePartyMaintenanceOptions];
        options.unshift({ name: "All", value: "total" });
        return options;
      })(),
    },
  ],
};
